import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { HasProductContext, withProductContext } from "@insite/client-framework/Components/ProductContext";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import setAddToListModalIsOpen from "@insite/client-framework/Store/Components/AddToListModal/Handlers/SetAddToListModalIsOpen";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getLocation } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import addToWishList from "@insite/client-framework/Store/Data/WishLists/Handlers/AddToWishList";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import Icon, { IconPresentationProps } from "@insite/mobius/Icon";
import Link, { LinkPresentationProps, StyledIcon } from "@insite/mobius/Link";
import Modal, { ModalPresentationProps } from "@insite/mobius/Modal";
import ToasterContext from "@insite/mobius/Toast/ToasterContext";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import CustomHeartIcon from "@tSurya/Icons/CustomHeartIcon";
import { buttonStyles } from "@tSurya/StylesExtensions/IconStyle";
import { COLORS, TYPOGRAPHY } from "@tSurya/theme/design";
import * as React from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

interface OwnProps {
    labelOverride?: string;
    extendedStyles?: ProductAddToListLinkStyles;
    showIcon?: boolean;
    fromPDP?: boolean;
}

type Props = OwnProps &
    ReturnType<typeof mapStateToProps> &
    ResolveThunks<typeof mapDispatchToProps> &
    HasProductContext;

const mapStateToProps = (state: ApplicationState) => ({
    wishListSettings: getSettingsCollection(state).wishListSettings,
    variantSelectionCompleted: state.pages.productDetails.variantSelectionCompleted,
    signInUrl: getPageLinkByPageType(state, "SignInPage")?.url,
    location: getLocation(state),
});

const mapDispatchToProps = {
    setAddToListModalIsOpen,
    addToWishList,
};

export interface ProductAddToListLinkStyles {
    link?: InjectableCss;
    wrapper?: InjectableCss;
    heartIcon?: InjectableCss;
    button?: InjectableCss;
    heartIconStyles?:IconPresentationProps;
    innerWrapper?: InjectableCss;
    modal?: ModalPresentationProps;
    SizeMessageText?: TypographyProps;
}

export const productAddToListLinkStyles: ProductAddToListLinkStyles = {
    wrapper: {
        css: css`
            text-align: center;
            display: flex;
            background: ${getColor("common.background")};
            border: 1px solid ${getColor("primary")};
            padding: 10px 40px;
            display: flex;
            justify-content: center;
            cursor: pointer;
            align-items: center;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        margin-left: 0px;
                    `,

                    css`
                        margin-left: 0px;
                    `,

                    css`
                        margin-left: 0px;
                    `,

                    null,

                    null,
                ])}
        `,
    },
    button: {
        css: css`
            margin-left: 15px;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        margin-left: 5px;
                    `,

                    css`
                        margin-left: 5px;
                    `,

                    css`
                        margin-left: 10px;
                    `,
                    css`
                        margin-left: 15px;
                    `,
                    css`
                        margin-left: 15px;
                    `,
                ])}
            white-space: nowrap;
        `,
    },
    link: {
        css: css`
            outline: none !important;
            span {
                font-family: ${TYPOGRAPHY.fontFamily.title};
                font-style: normal;
                font-weight: ${TYPOGRAPHY.weight.bold};
                font-size: ${TYPOGRAPHY.size.base};
                line-height: ${TYPOGRAPHY.lineHeight.xbase};
                text-align: center;
                color: ${getColor("text.main")}!important;
            }
        `,
    },
    heartIcon: {
        css: css`
            display: flex;
        `,
    },
    heartIconStyles:{
        size:16,
    },
    modal: {
        size: 350,
        cssOverrides: {
            modalBody: css`
                overflow-y: visible;
            `,
            modalTitle: css`
                padding: 10px 20px;
            `,
            modalContent: css`
                padding: 20px;
            `,
        },
    },
};

/**
 * @deprecated Use productAddToListLinkStyles instead.
 */
export const productAddToListLink = productAddToListLinkStyles;

const ProductAddToListLink: React.FC<Props> = ({
    productContext: { product, productInfo },
    wishListSettings,
    labelOverride,
    setAddToListModalIsOpen,
    addToWishList,
    extendedStyles,
    fromPDP =false,
    showIcon = false,
    variantSelectionCompleted,
    signInUrl,
    location,
    ...otherProps
}) => {
    const toasterContext = React.useContext(ToasterContext);
    const [styles] = React.useState(() => mergeToNew(productAddToListLink, extendedStyles));
    const [modalIsOpen, setModalIsOpen] = React.useState(false);

    if (product.id.includes("Temp")) {
        return null;
    }
    const addToListLinkClickHandler = () => {
        if (!wishListSettings.allowMultipleWishLists) {
            addToWishList({
                productInfos: [productInfo],
                onSuccess: () => {
                    toasterContext.addToast({ body: siteMessage("Lists_ProductAdded"), messageType: "success" });
                },
                onComplete(resultProps) {
                    if (resultProps.result?.wishList) {
                        // "this" is targeting the object being created, not the parent SFC
                        // eslint-disable-next-line react/no-this-in-sfc
                        this.onSuccess?.(resultProps.result?.wishList);
                    } else if (resultProps.result?.errorMessage) {
                        toasterContext.addToast({ body: resultProps.result?.errorMessage, messageType: "danger" });
                    }
                },
            });
            return;
        }
        if(fromPDP)
        {
            if(variantSelectionCompleted){
                setAddToListModalIsOpen({ modalIsOpen: true, productInfos: [productInfo] });
            }
            else{
                setModalIsOpen(true);
            }
        }
        else{
            setAddToListModalIsOpen({ modalIsOpen: true, productInfos: [productInfo] });
        }
    };
    const customStyleButton = buttonStyles;
    const modalCloseHandler = () => {
        setModalIsOpen(false);
    };
    const returnUrl = location.pathname + location.search;
    return (
        <>
            {!showIcon ? (
                // <StyledWrapper onClick={addToListLinkClickHandler} {...styles.wrapper}>
                //     <StyledWrapper {...styles.heartIcon}>
                //         <Icon src={CustomHeartIcon}></Icon>
                //     </StyledWrapper>
                //     <StyledWrapper {...styles.button}>
                //         <Link {...styles.link} disabled={productInfo.qtyOrdered <= 0} {...otherProps}>
                //             {labelOverride ?? translate("Add to Project")}
                //         </Link>
                //     </StyledWrapper>
                // </StyledWrapper>
                <Link
                aria-label="Add to Project"
                {...customStyleButton.iconSecondryLightBackground}
                disabled={productInfo.qtyOrdered <= 0}
                onClick={addToListLinkClickHandler}
            >
                <StyledIcon src={CustomHeartIcon} size={15}  />
                {labelOverride ?? translate("Add to Project")}
            </Link>
            ) : (
                <Icon src={CustomHeartIcon} onClick={addToListLinkClickHandler} {...styles.heartIconStyles}></Icon>
            )}
           { modalIsOpen && <Modal
                {...styles.modal}
                headline={translate("Select Size")}
                isOpen={modalIsOpen}
                handleClose={modalCloseHandler}
              >
                <StyledWrapper {...styles.innerWrapper} data-test-selector="productAddToListModal">
                    <Typography data-test-selector="productAddToListModal_requireSignIn" {...styles.SizeMessageText}>
                            {siteMessage(
                                "List_Must_Select_Size",
                                `${signInUrl}?returnUrl=${encodeURIComponent(returnUrl)}&clientRedirect=true`,
                            )}
                        </Typography>

                </StyledWrapper>
              </Modal>
           }
        </>
    );
};

export default connect(mapStateToProps, mapDispatchToProps)(withProductContext(ProductAddToListLink));
